import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  adminLogin,
  fetchLeads,
  fetchStats,
  updateLeadStatus,
  deleteLead,
} from "@/lib/api";
import Logo from "@/components/site/Logo";
import {
  Loader2,
  LogOut,
  Mail,
  Phone,
  Trash2,
  Lock,
  Inbox,
  ExternalLink,
} from "lucide-react";

const SESSION_KEY = "bgs_admin_pw";

const STATUS_COLORS = {
  new: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  contacted: "bg-sky-500/15 text-sky-300 border-sky-400/30",
  won: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  lost: "bg-red-500/15 text-red-300 border-red-400/30",
};

const TYPE_LABEL = {
  free_preview: "Free Preview",
  quote: "Quote",
  contact: "Contact",
};

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      setPassword(saved);
      verifyAndLoad(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function verifyAndLoad(pw) {
    setLoading(true);
    try {
      await adminLogin(pw);
      sessionStorage.setItem(SESSION_KEY, pw);
      setAuthed(true);
      await Promise.all([loadLeads(pw), loadStats(pw)]);
    } catch (err) {
      sessionStorage.removeItem(SESSION_KEY);
      setAuthed(false);
      if (err?.response?.status === 401) {
        toast.error("Invalid password");
      } else {
        toast.error("Login failed");
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadLeads(pw = password) {
    try {
      const data = await fetchLeads(pw);
      setLeads(data);
    } catch (err) {
      toast.error("Failed to load leads");
      console.error(err);
    }
  }

  async function loadStats(pw = password) {
    try {
      const s = await fetchStats(pw);
      setStats(s);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleStatusChange(id, status) {
    try {
      await updateLeadStatus(password, id, status);
      toast.success("Status updated");
      await Promise.all([loadLeads(), loadStats()]);
    } catch {
      toast.error("Update failed");
    }
  }

  async function handleDelete(id) {
    try {
      await deleteLead(password, id);
      toast.success("Lead deleted");
      await Promise.all([loadLeads(), loadStats()]);
    } catch {
      toast.error("Delete failed");
    }
  }

  function logout() {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthed(false);
    setPassword("");
    setLeads([]);
    setStats(null);
  }

  const filtered = filter === "all" ? leads : leads.filter((l) => l.type === filter);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] text-white grid place-items-center px-5">
        <Toaster theme="dark" position="bottom-right" />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0c10] p-8 relative overflow-hidden"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" />
          <div className="flex items-center gap-3">
            <Logo showWordmark={false} />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-semibold">
                Admin Console
              </p>
              <h1 className="font-display text-2xl font-semibold tracking-tight">
                Website Walsh · Leads
              </h1>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              verifyAndLoad(password);
            }}
            className="mt-7 space-y-4"
          >
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-white/50">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  required
                  autoFocus
                  placeholder="Enter admin password"
                  className="pl-10 bg-black/40 border-white/10 focus-visible:ring-emerald-400/40 focus-visible:border-emerald-400/50"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !password}
              data-testid="admin-login-submit"
              className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-black font-bold rounded-full py-5"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-5 text-xs text-white/40 text-center">
            Restricted area · Website Walsh internal use only.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster theme="dark" position="bottom-right" />

      <header className="border-b border-white/5 bg-[#070809]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo showWordmark={false} />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-semibold">
                Admin · Leads
              </p>
              <p className="font-display text-sm text-white/70">Website Walsh Control Room</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1"
              data-testid="admin-back-home"
            >
              View site <ExternalLink className="w-3 h-3" />
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="admin-logout"
              className="border-white/15 bg-transparent text-white hover:bg-white/5"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile label="Total leads" value={stats?.total ?? "–"} />
          <StatTile label="Free Preview" value={stats?.by_type?.free_preview ?? "–"} />
          <StatTile label="Quote" value={stats?.by_type?.quote ?? "–"} />
          <StatTile label="New (unread)" value={stats?.by_status?.new ?? "–"} highlight />
        </div>

        {/* Filters */}
        <Tabs value={filter} onValueChange={setFilter} className="w-full">
          <TabsList className="bg-[#0a0c10] border border-white/10">
            <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
            <TabsTrigger value="free_preview" data-testid="filter-preview">Free Preview</TabsTrigger>
            <TabsTrigger value="quote" data-testid="filter-quote">Quote</TabsTrigger>
            <TabsTrigger value="contact" data-testid="filter-contact">Contact</TabsTrigger>
          </TabsList>
          <TabsContent value={filter} className="mt-6">
            <LeadsTable
              leads={filtered}
              onStatus={handleStatusChange}
              onDelete={handleDelete}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatTile({ label, value, highlight }) {
  return (
    <div
      className={`rounded-2xl border p-5 bg-[#0a0c10] ${
        highlight ? "border-emerald-400/30" : "border-white/10"
      }`}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-semibold">
        {label}
      </p>
      <p className="mt-2 font-display text-3xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function LeadsTable({ leads, onStatus, onDelete }) {
  if (leads.length === 0) {
    return (
      <div
        data-testid="leads-empty"
        className="rounded-2xl border border-white/10 bg-[#0a0c10] p-16 text-center"
      >
        <Inbox className="w-8 h-8 text-white/30 mx-auto" />
        <p className="mt-4 font-display text-lg text-white/70">No leads yet</p>
        <p className="text-sm text-white/40">
          Submissions will appear here in real-time.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0c10] overflow-hidden" data-testid="leads-table">
      <Table>
        <TableHeader>
          <TableRow className="border-white/5 hover:bg-transparent">
            <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.18em]">
              Lead
            </TableHead>
            <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.18em]">
              Type
            </TableHead>
            <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.18em]">
              Contact
            </TableHead>
            <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.18em]">
              Details
            </TableHead>
            <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.18em]">
              Status
            </TableHead>
            <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.18em] text-right">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((l) => (
            <TableRow key={l.id} className="border-white/5" data-testid={`lead-row-${l.id}`}>
              <TableCell className="align-top">
                <p className="font-display text-base font-semibold text-white">
                  {l.business_name}
                </p>
                <p className="text-xs text-white/40 mt-0.5">
                  {new Date(l.created_at).toLocaleString()}
                </p>
                {l.business_type && (
                  <p className="text-xs text-emerald-300/80 mt-1">{l.business_type}</p>
                )}
              </TableCell>
              <TableCell className="align-top">
                <Badge className="border bg-white/5 text-white/80 border-white/10 font-medium">
                  {TYPE_LABEL[l.type] || l.type}
                </Badge>
              </TableCell>
              <TableCell className="align-top">
                <p className="text-sm text-white/85">{l.contact_name}</p>
                <a
                  href={`mailto:${l.email}`}
                  className="text-xs text-emerald-300 hover:underline inline-flex items-center gap-1 mt-1"
                >
                  <Mail className="w-3 h-3" /> {l.email}
                </a>
                {l.phone && (
                  <a
                    href={`tel:${l.phone}`}
                    className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1 mt-1 block"
                  >
                    <Phone className="w-3 h-3" /> {l.phone}
                  </a>
                )}
              </TableCell>
              <TableCell className="align-top max-w-xs">
                {l.location && (
                  <p className="text-xs text-white/55">📍 {l.location}</p>
                )}
                {l.current_website && (
                  <a
                    href={l.current_website.startsWith("http") ? l.current_website : `https://${l.current_website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-emerald-300 hover:underline break-all"
                  >
                    {l.current_website}
                  </a>
                )}
                {l.message && (
                  <p className="text-xs text-white/65 mt-1 line-clamp-3">{l.message}</p>
                )}
              </TableCell>
              <TableCell className="align-top">
                <Select
                  value={l.status}
                  onValueChange={(v) => onStatus(l.id, v)}
                >
                  <SelectTrigger
                    data-testid={`lead-status-trigger-${l.id}`}
                    className={`h-8 text-xs px-2.5 border ${STATUS_COLORS[l.status] || ""}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0c10] border-white/10 text-white">
                    {["new", "contacted", "won", "lost"].map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        className="capitalize focus:bg-emerald-500/10 focus:text-white"
                      >
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="align-top text-right">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`lead-delete-${l.id}`}
                      className="h-8 w-8 text-white/40 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0a0c10] border-white/10 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
                      <AlertDialogDescription className="text-white/60">
                        This permanently removes <strong className="text-white">{l.business_name}</strong>'s
                        submission. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/15 hover:bg-white/5 text-white">
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDelete(l.id)}
                        data-testid={`lead-delete-confirm-${l.id}`}
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-400/30"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
